import { CommonModule } from '@angular/common';
import { Component, computed, input, OnDestroy, OnInit, Optional, signal } from '@angular/core';
import { Feature, Map as OlMap, View } from 'ol';
import { Geometry } from 'ol/geom';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { XYZ } from 'ol/source';
import IGC from 'ol/format/IGC.js';
import KML from 'ol/format/KML.js';
import GPX from 'ol/format/GPX.js';
import TopoJSON from 'ol/format/TopoJSON.js';
import GeoJSON from 'ol/format/GeoJSON.js';

import VectorSource from 'ol/source/Vector';
import { unzipSync } from 'fflate';
import { environment } from 'src/environments/environment';
import { transform } from 'ol/proj';
import { inferType, isHTML } from 'src/app/utils/string.utils';
import { Fill, Stroke, Style, Text } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MaterialModule } from 'src/app/shared/modules/material.module';
import { PageEvent } from '@angular/material/paginator';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { defaults as defaultInteractions } from 'ol/interaction';
import { ImportWizardService } from '../../../import-wizard.service';
import { ImportWizardStorage, ImportWizardStep, ImportWizardType, TabularColumns } from '../../../import-wizard.storage';
import { FeatureLike } from 'ol/Feature';
import { MatDialogRef } from '@angular/material/dialog';
import { ImportWizardComponent } from '../../../import-wizard.component';
import { ImportWizardGeoSpatialService } from '../import-wizard-geospatial.service';
import { firstValueFrom } from 'rxjs';
import { AlertComponent } from 'src/app/shared/alert/alert.component';
import { ImportWizardGeoSpatialMiniMapComponent } from '../_components/minimap/import-wizard-geospatial-minimap.component';
import { JobStatus } from 'src/app/dtos/import-wizard/import-wizard.dto';
import { ToastrService } from 'ngx-toastr';

interface FeatureInfo {
  description?: string | SafeHtml;
  name?: string;
  _name?: string;
  layer?: string;
  properties: Record<string, any>;
}

@Component({
    selector: 'app-import-wizard-geospatial-preview',
    templateUrl: './import-wizard-geospatial-preview.component.html',
    styleUrls: ['./import-wizard-geospatial-preview.component.scss'],
    imports: [
        CommonModule,
        MaterialModule,
        ScrollingModule,
        AlertComponent,
        ImportWizardGeoSpatialMiniMapComponent
    ],
    providers: [],
    standalone: true
})
export class ImportWizardGeospatialPreviewComponent implements OnInit, OnDestroy {
    _state = computed(() => this.importWizardStorage.state());

    // DATA
    featureRows = computed<FeatureInfo[]>(() => this.importWizardStorage.rows());
    featureTableDataSource = computed(() => {
        const start = (this.currentPage() - 1) * this.pageSize();
        const end = start + this.pageSize();

        if(this.searchTerm() === '') return this.featureRows().slice(start, end);

        return this.featureRows().filter((row) => this.featureMatchesSearch(row)).slice(start, end);
    });
    fileTotalRows = computed(() => this.importWizardStorage.fileTotalRows());

    columns = computed(() => this.importWizardStorage.columns());
    columns_names = computed(() => this.columns().map((col) => col.name));

    // PAGINATION
    pagesSizes = { 
        'min': 5,
        'mid-lower': 10,
        'mid-upper': 25,
        'max': 100
    }
    pagesSizesArray = Object.values(this.pagesSizes);
    pageSize = signal(this.pagesSizes['mid-lower']);
    currentPage = signal(1);
    total = computed(() => this.featureRows().filter((row) => this.featureMatchesSearch(row)).length);
    
    private searchTerm = signal<string>('');

    isAlertVisible = false;
    isLoading = signal<boolean>(true);

    constructor(
        private importWizardStorage: ImportWizardStorage,
        private importWizardService: ImportWizardService,
        private toastrService: ToastrService,
        private sanitizer: DomSanitizer,
        @Optional() private dialogRef: MatDialogRef<ImportWizardComponent>
    ) {}

    async ngOnInit() {}

    private featureUsesName(): boolean {
        return this.featureRows().some(feature => feature.name || feature._name);
    }

    search(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.searchTerm.set(target.value);
    }

    handlePageChange(event: PageEvent): void {
        this.currentPage.set(event.pageIndex + 1);
        this.pageSize.set(event.pageSize);
    }

    featureMatchesSearch(feature: any): boolean {
        const term = this.searchTerm().toLowerCase();

        const currentFeatureFormat = feature.properties || feature

        if (this.featureUsesName()) { 
            const name = (currentFeatureFormat.name || currentFeatureFormat._name) ?? '';
            return name?.toLowerCase().includes(term);
        } else { 
            return Object.values(currentFeatureFormat)
                .some(value => String(value).toLowerCase().includes(term));
        }
    }

    handleButtonClick(): void {
         if(!this.isPreviewStep) {
            this.importWizardStorage.redirectInViewStep(ImportWizardStep.MAPPING_ADJUSTS);
            return;
        }
        
        this.importWizardService.patchImportWizard(this._state().id, JobStatus.PENDING).subscribe({ 
            next: () => {
                this.importWizardStorage.setNextStep();
            },
            error: () => {
                this.toastrService.error('Erro ao iniciar importação!', 'Erro');
            }
        });
    }

    handleCancelClick(): void {
         if(this._state().MultiFeatured) {
            this.importWizardStorage.setStep(ImportWizardStep.FEATURES_LIST);
            this.importWizardStorage.redirectInViewStep(ImportWizardStep.FEATURES_LIST);
            return;
        }

        this.isAlertVisible = true;
    }

    handleCancelClickConfirmation(): void {
        this.isAlertVisible = false;

        this.importWizardStorage.handleCancelClickConfirmation(this.dialogRef);
    }

    handleCancelClickCancel(): void {
        this.isAlertVisible = false;
    }

    get isPreviewStep(): boolean {
        return this._state().step === ImportWizardStep.PREVIEW;
    }

    ngOnDestroy(): void {
        this.importWizardStorage.setFilename('');
    }

    ImportWizardStep = ImportWizardStep;

    isHTML = isHTML;
}